import { c as _c } from "react-compiler-runtime";
import { createContext, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldIds } from "@/components/form";
import Select, { type SelectOption, type SelectProps } from "@/components/form/SelectField/Select";
import { cn } from "@/core/utils";
type SelectFieldContextValue = {
  inputId: string;
  labelId: string;
  hintId: string;
  errorId: string;
};
const SelectFieldContext = createContext<SelectFieldContextValue | null>(null);
function useSelectFieldContext() {
  const ctx = useContext(SelectFieldContext);
  if (!ctx) {
    throw new globalThis.Error("SelectField components must be used within SelectField.Root");
  }
  return ctx;
}
type SelectLabelProps = {
  text: string;
  invalid?: boolean;
  hideLabel?: boolean;
};
function SelectLabel(t0) {
  const $ = _c(10);
  const {
    text,
    invalid,
    hideLabel
  } = t0;
  const {
    inputId,
    labelId
  } = useSelectFieldContext();
  const t1 = hideLabel && "sr-only";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("grid", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== inputId || $[3] !== invalid || $[4] !== labelId || $[5] !== text) {
    t3 = <Label htmlFor={inputId} id={labelId} invalid={invalid} text={text} />;
    $[2] = inputId;
    $[3] = invalid;
    $[4] = labelId;
    $[5] = text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
type SelectHintProps = {
  text: string;
};
function SelectHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useSelectFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type SelectErrorMessageProps = {
  text: string;
};
function SelectErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    inputId,
    errorId
  } = useSelectFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== inputId || $[2] !== text) {
    t1 = <ErrorMessage htmlFor={inputId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = inputId;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  children: React.ReactNode;
  name: string;
};
function Root(t0) {
  const $ = _c(10);
  const {
    children,
    name
  } = t0;
  const fieldIds = useFieldIds(name);
  let t1;
  if ($[0] !== fieldIds.errorId || $[1] !== fieldIds.hintId || $[2] !== fieldIds.inputId || $[3] !== fieldIds.labelId) {
    t1 = {
      inputId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId
    };
    $[0] = fieldIds.errorId;
    $[1] = fieldIds.hintId;
    $[2] = fieldIds.inputId;
    $[3] = fieldIds.labelId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const value = t1;
  let t2;
  if ($[5] !== children) {
    t2 = <div className="flex flex-col gap-1 text-left">{children}</div>;
    $[5] = children;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2 || $[8] !== value) {
    t3 = <SelectFieldContext.Provider value={value}>{t2}</SelectFieldContext.Provider>;
    $[7] = t2;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
type FieldChromeProps = {
  hint?: string;
  error?: string;
  hideValidation?: boolean;
};
function FieldChrome(t0) {
  const $ = _c(8);
  const {
    hint,
    error,
    hideValidation
  } = t0;
  if (hideValidation) {
    return null;
  }
  let t1;
  if ($[0] !== error || $[1] !== hint) {
    t1 = hint && !error && <SelectHint text={hint} />;
    $[0] = error;
    $[1] = hint;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== error) {
    t2 = error && <SelectErrorMessage text={error} />;
    $[3] = error;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="h-2">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
type SelectInputProps = Omit<SelectProps, "aria-describedby" | "id">;
function SelectInput(props) {
  const $ = _c(5);
  const {
    errorId,
    hintId,
    inputId
  } = useSelectFieldContext();
  const invalid = !!props["aria-invalid"] || false;
  const t0 = `${hintId} ${errorId}`;
  const t1 = invalid || undefined;
  let t2;
  if ($[0] !== inputId || $[1] !== props || $[2] !== t0 || $[3] !== t1) {
    t2 = <Select {...props} aria-describedby={t0} aria-invalid={t1} id={inputId} />;
    $[0] = inputId;
    $[1] = props;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type SharedProps = {
  label: string;
  hint?: string;
  placeholder?: string;
  options: SelectOption[];
  direction?: "up" | "down";
  disabled?: boolean;
  showAddButton?: boolean;
  addButtonText?: string;
  hideLabel?: boolean;
  hideValidation?: boolean;
  onChange?: (option: SelectOption | null) => void;
  onAddNewOption?: () => void;
  /** If true, the field will only store the value of the selected option. onChange will still receive the full option object. */
  valueOnly?: boolean;
  variant?: "default" | "v3";
  className?: string;
};
type NativeProps = SharedProps & {
  name: string;
  value?: SelectOption | null;
  onValueChange: (option: SelectOption | null) => void;
};
function Native(t0) {
  const $ = _c(31);
  const {
    name,
    label,
    hint,
    placeholder,
    options,
    direction,
    disabled: t1,
    showAddButton: t2,
    addButtonText: t3,
    hideLabel: t4,
    hideValidation: t5,
    onChange,
    onAddNewOption,
    valueOnly: t6,
    variant: t7,
    className,
    value,
    onValueChange
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const showAddButton = t2 === undefined ? false : t2;
  const addButtonText = t3 === undefined ? "Add New" : t3;
  const hideLabel = t4 === undefined ? false : t4;
  const hideValidation = t5 === undefined ? false : t5;
  t6 === undefined ? false : t6;
  const variant = t7 === undefined ? "default" : t7;
  let t8;
  if ($[0] !== onChange || $[1] !== onValueChange) {
    t8 = selected => {
      onValueChange(selected);
      onChange?.(selected);
    };
    $[0] = onChange;
    $[1] = onValueChange;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const handleSelect = t8;
  const t9 = disabled && "pointer-events-none opacity-50";
  let t10;
  if ($[3] !== className || $[4] !== t9) {
    t10 = cn("SelectField grid gap-2", t9, className);
    $[3] = className;
    $[4] = t9;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  let t11;
  if ($[6] !== hideLabel || $[7] !== label) {
    t11 = <SelectLabel hideLabel={hideLabel} text={label} />;
    $[6] = hideLabel;
    $[7] = label;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const t12 = value ?? undefined;
  let t13;
  if ($[9] !== addButtonText || $[10] !== direction || $[11] !== disabled || $[12] !== handleSelect || $[13] !== onAddNewOption || $[14] !== options || $[15] !== placeholder || $[16] !== showAddButton || $[17] !== t12 || $[18] !== variant) {
    t13 = <SelectInput addButtonText={addButtonText} aria-invalid={false} direction={direction} disabled={disabled} options={options} placeholder={placeholder} showAddButton={showAddButton} value={t12} variant={variant} onAddNewOption={onAddNewOption} onSelect={handleSelect} />;
    $[9] = addButtonText;
    $[10] = direction;
    $[11] = disabled;
    $[12] = handleSelect;
    $[13] = onAddNewOption;
    $[14] = options;
    $[15] = placeholder;
    $[16] = showAddButton;
    $[17] = t12;
    $[18] = variant;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== hideValidation || $[21] !== hint) {
    t14 = <FieldChrome hideValidation={hideValidation} hint={hint} />;
    $[20] = hideValidation;
    $[21] = hint;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t13 || $[26] !== t14) {
    t15 = <div className={t10} data-testid="SelectField">{t11}{t13}{t14}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== name || $[29] !== t15) {
    t16 = <Root name={name}>{t15}</Root>;
    $[28] = name;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
}
type RHFProps<T extends FieldValues> = SharedProps & {
  name: Path<T>;
};
function RHF(t0) {
  const $ = _c(44);
  const {
    name,
    label,
    hint,
    placeholder,
    options: t1,
    direction,
    disabled: t2,
    showAddButton: t3,
    addButtonText: t4,
    hideLabel: t5,
    hideValidation: t6,
    onChange,
    onAddNewOption,
    valueOnly: t7,
    variant: t8,
    className
  } = t0;
  let t9;
  if ($[0] !== t1) {
    t9 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const options = t9;
  const disabled = t2 === undefined ? false : t2;
  const showAddButton = t3 === undefined ? false : t3;
  const addButtonText = t4 === undefined ? "Add New" : t4;
  const hideLabel = t5 === undefined ? false : t5;
  const hideValidation = t6 === undefined ? false : t6;
  const valueOnly = t7 === undefined ? false : t7;
  const variant = t8 === undefined ? "default" : t8;
  let t10;
  if ($[2] !== name) {
    t10 = {
      name
    };
    $[2] = name;
    $[3] = t10;
  } else {
    t10 = $[3];
  }
  const {
    field,
    fieldState
  } = useController(t10);
  const invalid = !!fieldState.error?.message;
  let t11;
  if ($[4] !== field || $[5] !== onChange || $[6] !== valueOnly) {
    t11 = selected => {
      if (valueOnly) {
        field.onChange(selected?.value ?? null);
      } else {
        field.onChange(selected);
      }
      onChange?.(selected);
    };
    $[4] = field;
    $[5] = onChange;
    $[6] = valueOnly;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const onSelect = t11;
  const currentValue = typeof field.value === "object" ? (field.value as SelectOption).value : field.value as unknown;
  let t12;
  if ($[8] !== currentValue || $[9] !== options) {
    let t13;
    if ($[11] !== currentValue) {
      t13 = opt => opt.value === currentValue;
      $[11] = currentValue;
      $[12] = t13;
    } else {
      t13 = $[12];
    }
    t12 = options.find(t13);
    $[8] = currentValue;
    $[9] = options;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const selectedOption = t12;
  const t13 = disabled && "pointer-events-none opacity-50";
  let t14;
  if ($[13] !== className || $[14] !== t13) {
    t14 = cn("SelectField grid gap-2", t13, className);
    $[13] = className;
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== hideLabel || $[17] !== invalid || $[18] !== label) {
    t15 = <SelectLabel hideLabel={hideLabel} invalid={invalid} text={label} />;
    $[16] = hideLabel;
    $[17] = invalid;
    $[18] = label;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== addButtonText || $[21] !== direction || $[22] !== disabled || $[23] !== invalid || $[24] !== onAddNewOption || $[25] !== onSelect || $[26] !== options || $[27] !== placeholder || $[28] !== selectedOption || $[29] !== showAddButton || $[30] !== variant) {
    t16 = <SelectInput addButtonText={addButtonText} aria-invalid={invalid} direction={direction} disabled={disabled} options={options} placeholder={placeholder} showAddButton={showAddButton} value={selectedOption} variant={variant} onAddNewOption={onAddNewOption} onSelect={onSelect} />;
    $[20] = addButtonText;
    $[21] = direction;
    $[22] = disabled;
    $[23] = invalid;
    $[24] = onAddNewOption;
    $[25] = onSelect;
    $[26] = options;
    $[27] = placeholder;
    $[28] = selectedOption;
    $[29] = showAddButton;
    $[30] = variant;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = fieldState.error?.message;
  let t18;
  if ($[32] !== hideValidation || $[33] !== hint || $[34] !== t17) {
    t18 = <FieldChrome error={t17} hideValidation={hideValidation} hint={hint} />;
    $[32] = hideValidation;
    $[33] = hint;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t16 || $[39] !== t18) {
    t19 = <div className={t14} data-testid="SelectField">{t15}{t16}{t18}</div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== name || $[42] !== t19) {
    t20 = <Root name={name}>{t19}</Root>;
    $[41] = name;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
}
export const SelectField = {
  RHF,
  Native
};